import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import WidgetCard from "../_shared/WidgetCard";
import { registerHeadOfficeCharts } from "../_shared/registerCharts";
import { fmt } from "../data";
import styles from "./style.module.scss";

registerHeadOfficeCharts();

export interface AssignmentStatus {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface AssignmentPipelineProps {
  /** the four assignment buckets — order drives the donut + legend */
  statuses: AssignmentStatus[];
  /** total assigned (donut centre); defaults to the sum of the buckets */
  total?: number;
  /** completed share shown under the total (0–100) */
  donePct?: number;
  onOpen?: () => void;
}

/**
 * Assignment status breakdown (Not Started / In Progress / Completed / Overdue)
 * as a donut with a legend. Head-office only.
 */
export default function AssignmentPipeline({
  statuses,
  total,
  donePct,
  onOpen,
}: AssignmentPipelineProps) {
  const sum = total ?? statuses.reduce((a, s) => a + s.value, 0);
  const done =
    donePct ??
    (sum
      ? Math.round(
          ((statuses.find((s) => s.key === "completed")?.value ?? 0) / sum) *
            100,
        )
      : 0);

  const { chartData, options } = useMemo(
    () => ({
      chartData: {
        labels: statuses.map((s) => s.label),
        datasets: [
          {
            data: statuses.map((s) => s.value),
            backgroundColor: statuses.map((s) => s.color),
            borderWidth: 2,
            borderColor: "#ffffff",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        cutout: "68%",
        plugins: { legend: { display: false } },
      } as any,
    }),
    [statuses],
  );

  const openBtn = onOpen ? (
    <button type="button" className={styles.openBtn} onClick={onOpen}>
      Open assignments
    </button>
  ) : undefined;

  if (!sum) {
    return (
      <WidgetCard title="Assignment Pipeline" stretch flush>
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>No data found</div>
          <div className={styles.emptyHint}>
            No books have been assigned in this period yet.
          </div>
        </div>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title="Assignment Pipeline" right={openBtn} stretch flush>
      <div className={styles.body}>
        <div className={styles.chartBox}>
          <Doughnut data={chartData} options={options} />
          <div className={styles.center}>
            <div className={styles.centerValue}>{fmt(sum)}</div>
            <div className={styles.centerSub}>
              <b style={{ color: "#000" }}>Assigned</b>
              <br /> {done}% done
            </div>
          </div>
        </div>
        <div className={styles.legend}>
          {statuses.map((s) => {
            const pct = sum ? Math.round((s.value / sum) * 100) : 0;
            return (
              <div className={styles.legendItem} key={s.key}>
                <span className={styles.dot} style={{ background: s.color }} />
                <span className={styles.legendName}>{s.label}</span>
                <span className={styles.legendValue}>{fmt(s.value)}</span>
                <span className={styles.legendPct}>{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </WidgetCard>
  );
}
